import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { formatEventDate, formatPrice, formatAmount } from "@/lib/format";
import EmptyState from "@/components/EmptyState";
import { Calendar, MapPin, Heart, Vote as VoteIcon, BarChart3, Building2, Share2, Copy, Check, BadgeCheck } from "lucide-react";
import { toast } from "sonner";

const ORG_TYPE_LABEL = {
  association: "Association",
  entreprise: "Entreprise",
  club: "Club / Communauté",
  independant: "Indépendant",
  autre: "Autre",
};

function Avatar({ org, size = 88 }) {
  if (org.logo_b64) {
    return <img src={org.logo_b64} alt={org.name} style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", border: "4px solid white", boxShadow: "0 4px 20px rgba(0,0,0,0.12)" }} />;
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", background: "var(--z-teal)",
      color: "white", display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.38, fontWeight: 700, border: "4px solid white",
      boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
    }}>
      {(org.name || "?")[0].toUpperCase()}
    </div>
  );
}

function EventTile({ e }) {
  return (
    <Link to={`/event/${e.id}`} className="z-event-card" data-testid={`pub-org-event-${e.id}`}>
      {e.image_url ? (
        <img src={e.image_url} alt="" className="z-event-card-img" />
      ) : (
        <div className="z-event-card-img" style={{ background: "linear-gradient(135deg,#01696F,#DA7101)" }} />
      )}
      <div className="z-event-card-body">
        <h3 className="z-event-card-title">{e.title}</h3>
        <p className="z-event-card-meta"><Calendar size={12} /> {formatEventDate(e.starts_at)}</p>
        {e.location && <p className="z-event-card-meta"><MapPin size={12} /> {e.location}</p>}
        <div className="z-event-card-price">{e.min_price > 0 ? `Dès ${formatPrice(e.min_price, e.currency)}` : "Gratuit"}</div>
      </div>
    </Link>
  );
}

function FundraiserTile({ f }) {
  const pct = f.goal > 0 ? Math.min(100, Math.round((f.raised / f.goal) * 100)) : 0;
  return (
    <Link to={`/fundraiser/${f.id}`} className="z-event-card" data-testid={`pub-org-fund-${f.id}`}>
      {f.image_url ? (
        <img src={f.image_url} alt="" className="z-event-card-img" />
      ) : (
        <div className="z-event-card-img" style={{ background: "linear-gradient(135deg,#DA7101,#01696F)" }} />
      )}
      <div className="z-event-card-body">
        <h3 className="z-event-card-title">{f.title}</h3>
        <div className="z-progress" style={{ margin: "8px 0 6px" }}>
          <div className="z-progress-fill" style={{ width: `${pct}%` }} />
        </div>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{formatAmount(f.raised, f.currency)} <span style={{ color: "var(--z-muted)", fontWeight: 400 }}>/ {formatAmount(f.goal, f.currency)}</span></div>
      </div>
    </Link>
  );
}

function VoteTile({ v }) {
  return (
    <Link to={`/vote/${v.id}`} className="z-card" style={{ padding: 14, display: "block", textDecoration: "none", color: "inherit" }} data-testid={`pub-org-vote-${v.id}`}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <VoteIcon size={16} color="var(--z-teal)" />
        <span className="z-chip z-chip-active" style={{ fontSize: 11 }}>{v.vote_type}</span>
      </div>
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2 }}>{v.title}</div>
      <div style={{ fontSize: 12, color: "var(--z-muted)" }}>{v.total_votes} votant(s)</div>
    </Link>
  );
}

function SurveyTile({ s }) {
  return (
    <Link to={`/survey/${s.id}`} className="z-card" style={{ padding: 14, display: "block", textDecoration: "none", color: "inherit" }} data-testid={`pub-org-survey-${s.id}`}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <BarChart3 size={16} color="var(--z-orange)" />
        <span className="z-chip" style={{ fontSize: 11, background: "var(--z-orange)", color: "white" }}>Sondage</span>
      </div>
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2 }}>{s.title}</div>
      <div style={{ fontSize: 12, color: "var(--z-muted)" }}>{s.responses_count} réponse(s)</div>
    </Link>
  );
}

export default function PublicOrganizerPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get(`/organizers/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: data?.name || "Organisateur Zevents", url });
      } else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        toast.success("Lien copié");
        setTimeout(() => setCopied(false), 2000);
      }
    } catch { /* user cancelled */ }
  };

  if (loading) return <div className="z-container" style={{ textAlign: "center" }}><span className="z-loader z-loader-dark" /></div>;
  if (notFound || !data) {
    return (
      <div className="z-container" data-testid="public-org-not-found">
        <EmptyState
          icon={<Building2 size={28} />}
          title="Organisateur introuvable"
          message="Cette page n'existe pas ou n'est plus active."
          testId="public-org-empty"
        />
      </div>
    );
  }

  return (
    <div data-testid="public-org-page">
      {/* Hero band */}
      <div style={{
        background: "linear-gradient(135deg, #01696F 0%, #023d40 100%)",
        padding: "40px 20px 28px",
        color: "white",
        position: "relative",
      }}>
        <div style={{ maxWidth: 980, margin: "0 auto", display: "flex", gap: 22, alignItems: "center", flexWrap: "wrap" }}>
          <Avatar org={data} />
          <div style={{ flex: 1, minWidth: 240 }}>
            <h1 style={{ margin: 0, fontSize: 28, fontWeight: 800, letterSpacing: "-0.5px", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }} data-testid="pub-org-name">
              {data.name}
              {data.verified && (
                <span data-testid="pub-org-verified" title="Organisateur certifié par Zevents" style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "rgba(255,255,255,0.18)", padding: "3px 10px", borderRadius: 999, fontSize: 13, fontWeight: 700 }}>
                  <BadgeCheck size={15} /> Certifié
                </span>
              )}
            </h1>
            <div style={{ marginTop: 6, opacity: 0.85, fontSize: 14, display: "flex", gap: 10, flexWrap: "wrap" }}>
              {data.org_type && <span>{ORG_TYPE_LABEL[data.org_type] || data.org_type}</span>}
              {data.city && <span>· <MapPin size={12} style={{ verticalAlign: "middle" }} /> {data.city}</span>}
              {data.member_since && <span>· Membre depuis {new Date(data.member_since).getFullYear()}</span>}
            </div>
          </div>
          <button onClick={share} className="z-btn" style={{ background: "white", color: "var(--z-teal)" }} data-testid="pub-org-share-btn">
            {copied ? <><Check size={14} /> Copié</> : <><Share2 size={14} /> Partager</>}
          </button>
        </div>
      </div>

      <div className="z-container" style={{ maxWidth: 980 }}>
        {data.bio && (
          <div className="z-card" style={{ padding: 22, marginBottom: 22 }}>
            <p style={{ margin: 0, lineHeight: 1.6, whiteSpace: "pre-wrap", fontSize: 15 }} data-testid="pub-org-bio">{data.bio}</p>
          </div>
        )}

        {/* Stats grid */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 26 }}>
          <Stat icon={<Calendar size={18} />} label="Événements" value={data.stats.events_count} />
          <Stat icon={<Heart size={18} />} label="Cagnottes" value={data.stats.fundraisers_count} />
          <Stat icon={<VoteIcon size={18} />} label="Votes" value={data.stats.votes_count} />
          <Stat icon={<BarChart3 size={18} />} label="Sondages" value={data.stats.surveys_count} />
        </div>

        {data.events.length > 0 && (
          <Section title="Événements" testId="pub-org-events-section">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 14 }}>
              {data.events.map((e) => <EventTile key={e.id} e={e} />)}
            </div>
          </Section>
        )}

        {data.fundraisers.length > 0 && (
          <Section title="Cagnottes" testId="pub-org-funds-section">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 14 }}>
              {data.fundraisers.map((f) => <FundraiserTile key={f.id} f={f} />)}
            </div>
          </Section>
        )}

        {data.votes.length > 0 && (
          <Section title="Votes en cours" testId="pub-org-votes-section">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 14 }}>
              {data.votes.map((v) => <VoteTile key={v.id} v={v} />)}
            </div>
          </Section>
        )}

        {data.surveys.length > 0 && (
          <Section title="Sondages" testId="pub-org-surveys-section">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 14 }}>
              {data.surveys.map((s) => <SurveyTile key={s.id} s={s} />)}
            </div>
          </Section>
        )}

        {data.events.length === 0 && data.fundraisers.length === 0 && data.votes.length === 0 && data.surveys.length === 0 && (
          <EmptyState
            icon={<Calendar size={28} />}
            title="Rien de nouveau pour l'instant"
            message="Cet organisateur n'a pas encore publié d'activité."
            testId="pub-org-no-content"
          />
        )}
      </div>
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="z-card" style={{ padding: 14, textAlign: "center" }}>
      <div style={{ color: "var(--z-teal)", marginBottom: 4 }}>{icon}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: "var(--z-text)" }}>{value}</div>
      <div style={{ fontSize: 12, color: "var(--z-muted)", textTransform: "uppercase", letterSpacing: 1 }}>{label}</div>
    </div>
  );
}

function Section({ title, children, testId }) {
  return (
    <section style={{ marginBottom: 28 }} data-testid={testId}>
      <h2 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 12px", color: "var(--z-text)" }}>{title}</h2>
      {children}
    </section>
  );
}
